import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  calcularQuadroIcamento,
  identificarSistemaIcamento,
  type CadeiaIcamento,
} from '../../dominio/tabletop/simulacaoIcamento'
import type { CenaTabletop } from '../../dominio/tabletop/tipos'
import { IconeFechar, IconePausar, IconeReiniciar, IconeReproduzir } from '../icones'

const virgula = (valor: number, casas = 1) => valor.toFixed(casas).replace('.', ',')

export interface PropsBarraTesteSistema {
  cena: CenaTabletop
  aoAtualizarCenaSimulada: (cena: CenaTabletop | null) => void
  aoFechar: () => void
}

/**
 * Régua interativa de teste do sistema de içamento e redução de força:
 * Permite dar Play para ver a corda puxando, o sistema comprimindo e a maca subindo;
 * pausar, avançar/retroceder pelo slider e ler as forças em tempo real.
 */
export function BarraTesteSistema({ cena, aoAtualizarCenaSimulada, aoFechar }: PropsBarraTesteSistema) {
  const [progresso, setProgresso] = useState(0) // 0 a 1
  const [tocando, setTocando] = useState(false)
  // no celular a barra nasce recolhida (só controles): as métricas abrem sob demanda
  const [detalhesAbertos, setDetalhesAbertos] = useState(
    () => !(typeof window !== 'undefined' && window.matchMedia?.('(max-width: 640px)').matches),
  )
  const animacaoRef = useRef<number | null>(null)
  const tempoAnteriorRef = useRef<number | null>(null)

  // Guarda a cena original para aplicar a interpolação a cada quadro
  const cenaOriginalRef = useRef(cena)

  // Identifica a cadeia montada na cena
  const cadeia: CadeiaIcamento = useMemo(() => identificarSistemaIcamento(cenaOriginalRef.current), [])

  const atualizarQuadro = useCallback(
    (p: number) => {
      const quadro = calcularQuadroIcamento(cenaOriginalRef.current, cadeia, p)
      aoAtualizarCenaSimulada(quadro)
    },
    [aoAtualizarCenaSimulada, cadeia],
  )

  // Loop de animação contínua ao dar Play
  useEffect(() => {
    if (!tocando || !cadeia.valido) {
      if (animacaoRef.current) cancelAnimationFrame(animacaoRef.current)
      tempoAnteriorRef.current = null
      return
    }

    // proporcional ao percurso: a rota inteira (todas as puxadas) é executada de uma vez
    const DURAÇÃO_TOTAL_MS = (cadeia.duracaoSugeridaS ?? 3.8) * 1000

    const passo = (tempoAtual: number) => {
      if (tempoAnteriorRef.current === null) tempoAnteriorRef.current = tempoAtual
      const dt = tempoAtual - tempoAnteriorRef.current
      tempoAnteriorRef.current = tempoAtual

      setProgresso((atual) => {
        const proximo = Math.min(1, atual + dt / DURAÇÃO_TOTAL_MS)
        atualizarQuadro(proximo)
        if (proximo >= 1) {
          setTocando(false)
          return 1
        }
        return proximo
      })

      animacaoRef.current = requestAnimationFrame(passo)
    }

    animacaoRef.current = requestAnimationFrame(passo)

    return () => {
      if (animacaoRef.current) cancelAnimationFrame(animacaoRef.current)
    }
  }, [tocando, cadeia.valido, cadeia.duracaoSugeridaS, atualizarQuadro])

  const alternarPlay = () => {
    if (!cadeia.valido) return
    if (progresso >= 1) {
      setProgresso(0)
      atualizarQuadro(0)
      setTocando(true)
    } else {
      setTocando((t) => !t)
    }
  }

  const reiniciar = () => {
    setTocando(false)
    setProgresso(0)
    atualizarQuadro(0)
  }

  const fechar = useCallback(() => {
    setTocando(false)
    aoAtualizarCenaSimulada(null)
    aoFechar()
  }, [aoAtualizarCenaSimulada, aoFechar])

  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') fechar()
    }
    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  }, [fechar])

  const aoMudarSlider = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTocando(false)
    const novo = Number(e.target.value) / 100
    setProgresso(novo)
    atualizarQuadro(novo)
  }

  const percursoM = cadeia.percursoTotalM ?? Math.hypot(...(cadeia.deslocamentoMaca ?? [0, 0, 0]))
  const subidaM = progresso * percursoM
  const cordaPuxadaM = subidaM * (cadeia.razao ?? 1)
  const ciclos = cadeia.ciclos ?? 1
  const puxadaAtual = Math.min(ciclos, Math.floor(progresso * ciclos) + 1)
  const concluido = progresso >= 1

  return (
    <div
      className="tt-banner-posicionamento tt-barra-teste-sistema"
      role="region"
      aria-label="Controles do Teste de Içamento e Redução"
    >
      <div className="tt-teste-sistema__cabecalho">
        <strong><IconeReproduzir /> Teste do Sistema de Içamento</strong>
        <span className="tt-teste-sistema__cabecalho-acoes">
          {cadeia.valido && (
            <button
              type="button"
              className="btn-acao-cad btn-acao-cad--secundario tt-teste-sistema__alternar"
              aria-expanded={detalhesAbertos}
              onClick={() => setDetalhesAbertos((a) => !a)}
            >
              {detalhesAbertos ? 'Ocultar dados' : 'Dados'}
            </button>
          )}
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--secundario tt-teste-sistema__fechar"
            onClick={fechar}
            title="Fechar (Esc)"
            aria-label="Fechar (Esc)"
          >
            <IconeFechar /><span className="tt-teste-sistema__fechar-rotulo">Fechar (Esc)</span>
          </button>
        </span>
      </div>
      {!cadeia.valido && <span className="aviso-corda">{cadeia.motivoInvalido}</span>}

      {cadeia.valido && (
        <>
          <div className="tt-teste-sistema__metricas" hidden={!detalhesAbertos}>
            <span className="badge-metrica">
              <strong>Vantagem:</strong> {cadeia.razao}:1
            </span>
            {cadeia.analiseVM && (
              <>
                <span className="badge-metrica">
                  <strong>Tração real:</strong> {virgula(cadeia.analiseVM.forcaRealKgf)} kgf
                  <span className="dica"> (ideal: {virgula(cadeia.analiseVM.forcaIdealKgf)} kgf)</span>
                </span>
                <span className="badge-metrica">
                  <strong>Ancoragem:</strong> {virgula(cadeia.analiseVM.cargaNaAncoragemKgf, 0)} kgf
                </span>
              </>
            )}
            {cadeia.analiseVM?.fusivel && (
              <span className="badge-metrica" title={cadeia.analiseVM.fusivel.resumo}>
                <strong>{cadeia.analiseVM.fusivel.tipo.fatorDeFreio ? 'Trava' : 'Fusível'}:</strong> {cadeia.analiseVM.fusivel.tipo.nome}
                {Number.isFinite(cadeia.analiseVM.fusivel.tipo.limiteKgf) && cadeia.analiseVM.fusivel.tipo.id !== 'nenhum' && ` (${cadeia.analiseVM.fusivel.tipo.limiteKgf} kgf)`}
              </span>
            )}
            <span className="badge-metrica">
              <strong>Subida:</strong> {virgula(subidaM, 2)} de {virgula(percursoM, 2)} m
            </span>
            {ciclos > 1 && (
              <span className="badge-metrica">
                <strong>Puxada:</strong> {puxadaAtual}/{ciclos}
              </span>
            )}
            <span className="badge-metrica">
              <strong>Corda puxada:</strong> {virgula(cordaPuxadaM, 2)} m
            </span>
          </div>

          {cadeia.analiseVM?.fusivel && (
            <p className="dica tt-teste-sistema__fusivel" hidden={!detalhesAbertos}>
              {cadeia.analiseVM.fusivel.tipo.fatorDeFreio ? 'Com a trava' : 'Se a carga travar'}: {cadeia.analiseVM.fusivel.resumo}.
            </p>
          )}

          <div className="tt-teste-sistema__controles">
            <button
              type="button"
              className={`btn-acao-cad ${tocando ? 'btn-acao-cad--secundario' : 'btn-acao-cad--destaque'}`}
              onClick={alternarPlay}
              title={tocando ? 'Pausar' : 'Executar o percurso completo'}
            >
              {tocando ? (
                <>
                  <IconePausar />Pausar
                </>
              ) : concluido ? (
                <>
                  <IconeReiniciar />Puxar de novo
                </>
              ) : (
                <>
                  <IconeReproduzir />Play
                </>
              )}
            </button>
            <button
              type="button"
              className="btn-acao-cad btn-acao-cad--secundario"
              onClick={reiniciar}
              title="Voltar maca e sistema à posição inicial"
            >
              <IconeReiniciar />Reiniciar
            </button>
            <label className="tt-teste-sistema__slider-container">
              <span className="dica">Progresso</span>
              <span className="slider-porcentagem">{Math.round(progresso * 100)}%</span>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={Math.round(progresso * 100)}
                onChange={aoMudarSlider}
                aria-label="Progresso do içamento"
              />
            </label>
          </div>
        </>
      )}

    </div>
  )
}
